<template>
  <form @submit.prevent="submit">
    <h1 class="container-title" :class="[`text-${titleAlignment}`]" v-if="!hideTitle">
      {{ title ? "" : company.name.charAt(0).toUpperCase() + company.name.slice(1) }}
      {{ title ? title : "Sign Up" }}
    </h1>
    <FormMessage :errors="errors" v-if="errors" />
    <div :class="wideLayout ? 'row' : ''">
      <FormInput
        name="firstName"
        v-model="signupFields.firstName"
        testId="first-name-input"
        :class="wideLayout ? 'col-md-6' : ''"
        placeholder="First Name"
        label="First Name *"
        :errors="validation.fields.firstName.errors"
        :dirty="validation.fields.firstName.dirty"
        :tabindex="1"
      />
      <FormInput
        name="lastName"
        v-model="signupFields.lastName"
        testId="last-name-input"
        :class="wideLayout ? 'col-md-6' : ''"
        placeholder="Last Name"
        label="Last Name *"
        :errors="validation.fields.lastName.errors"
        :dirty="validation.fields.lastName.dirty"
        :tabindex="2"
      />
    </div>
    <div :class="wideLayout ? 'row' : ''">
      <FormInput
        name="email"
        v-model="signupFields.email"
        type="email"
        testId="email-input"
        @input="emailChanged"
        :class="wideLayout ? 'col-md-6' : ''"
        placeholder="Email"
        label="Email *"
        :errors="validation.fields.email.errors"
        :dirty="validation.fields.email.dirty"
        :tabindex="3"
      />
      <FormPassword
        name="password"
        v-model="signupFields.password"
        type="password"
        testId="password-input"
        :class="wideLayout ? 'col-md-6' : ''"
        placeholder="Password"
        label="Password *"
        :errors="validation.fields.password.errors"
        :dirty="validation.fields.password.dirty"
        :strengthMeter="true"
        :tabindex="4"
        v-model:strength="passwordStrength"
      />
    </div>
    <template v-if="!wideLayout">
      <FormCheckbox
        v-if="gdpr"
        name="marketingEmails"
        v-model="signupFields.marketingEmails"
        testId="marketing-emails-input"
        label="Please send me occasional relevant information from Nexcess. I understand that I can easily unsubscribe at any time."
        :errors="validation.fields.marketingEmails.errors"
        :dirty="validation.fields.marketingEmails.dirty"
        :tabindex="5"
      />
      <FormCheckbox
        name="acceptTerms"
        v-model="signupFields.acceptTerms"
        testId="accept-terms-input"
        :label="`I have read and agree to the <a href='https://www.nexcess.net/policies/terms-of-service' target='_blank'>Terms of Service</a> and acknowledge receipt of the <a href='https://www.nexcess.net/policies/privacy-policy' target='_blank'>Privacy Policy</a>.`"
        :errors="validation.fields.acceptTerms.errors"
        :dirty="validation.fields.acceptTerms.dirty"
        :tabindex="6"
      />
    </template>
    <div class="mt-3" v-if="gdpr">
      By continuing, and in order for Nexcess to provide you Services, you agree to have your
      personal information transferred and stored in the United States.
    </div>
    <div class="form-actions">
      <div :class="`text-${buttonAlignment}`">
        <CFTurnstileBypass v-if="useTurnstile" v-model="turnstileBypass" />
        <CFTurnstile
          v-if="useTurnstile"
          ref="turnstileContainer"
          :sitekey="turnstileSiteKey"
          :loadTurnstileScript="true"
          :tabindex="7"
          @verify="onVerify"
          @expired="onExpired"
          class="turnstile-container"
        />
        <div v-if="wideLayout" class="text-start">
          <FormCheckbox
            v-if="gdpr"
            name="marketingEmails"
            class="mt-3"
            v-model="signupFields.marketingEmails"
            testId="marketing-emails-input"
            label="Please send me occasional relevant information from Nexcess. I understand that I can easily unsubscribe at any time."
            :errors="validation.fields.marketingEmails.errors"
            :dirty="validation.fields.marketingEmails.dirty"
            :tabindex="8"
          />
          <FormCheckbox
            name="acceptTerms"
            class="mt-3"
            v-model="signupFields.acceptTerms"
            testId="accept-terms-input"
            :label="`I have read and agree to the <a href='https://www.nexcess.net/policies/terms-of-service' target='_blank'>Terms of Service</a> and acknowledge receipt of the <a href='https://www.nexcess.net/policies/privacy-policy' target='_blank'>Privacy Policy</a>.`"
            :errors="validation.fields.acceptTerms.errors"
            :dirty="validation.fields.acceptTerms.dirty"
            :tabindex="9"
          />
          <div class="signup-links text-start mt-3">
            <div class="sign-up">
              Already have an account?
              <BtnLink @click="loginAction()" :tabindex="10">Login</BtnLink>
            </div>
          </div>
        </div>
        <Btn
          variant="primary"
          type="submit"
          name="signup-button"
          :disabled="!submittable"
          :loading="loading"
          :tabindex="13"
          data-test="signup-button"
        >
          {{ signupSubmitText ? signupSubmitText : "Sign Up" }}
        </Btn>
      </div>
      <div v-if="!wideLayout" class="signup-links">
        <div class="sign-up my-2 mt-4">
          Already have an account?
          <BtnLink @click="loginAction()" :tabindex="14">Login</BtnLink>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { signupMixin, signupSetup } from "@/utils/login";
import Btn from "@/components/display/Btn";
import BtnLink from "@/components/display/BtnLink";
import CFTurnstile from "@/components/misc/CFTurnstile/CFTurnstile";
import CFTurnstileBypass from "@/components/smart/forms/CFTurnstileBypass";
import { defineAsyncComponent } from "vue";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";

export default {
  name: "SignupForm",
  components: {
    Btn,
    BtnLink,
    FormCheckbox,
    FormInput,
    FormMessage,
    FormPassword: defineAsyncComponent(() =>
      import(/* webpackChunkName: "form-password" */ "@/components/display/form/FormPassword")
    ),
    CFTurnstile,
    CFTurnstileBypass,
  },
  mixins: [signupMixin],
  props: {
    title: String,
    titleAlignment: {
      type: String,
      default: "center",
    },
    buttonAlignment: {
      type: String,
      default: "center",
    },
    wideLayout: Boolean,
    hideTitle: Boolean,
    signupSubmitText: String,
  },
  setup: signupSetup,
};
</script>

<style lang="scss" scoped>
.signup-links {
  font-size: 0.75rem;
  text-align: center;
  .forgot {
    margin-bottom: 1rem;
  }
}
</style>
